import { useState, useEffect } from 'react';

/**
 * Custom hook for responsive design - tracks media query matches
 * @param {string} query - CSS media query string (e.g., '(min-width: 768px)')
 * @returns {boolean} - Whether the media query matches
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    // No window (SSR) or no matchMedia (jsdom): report no match.
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Create event listener
    const handler = (event) => {
      setMatches(event.matches);
    };

    // Add listener (modern API with fallback)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handler);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    };
  }, [query]);

  return matches;
}

/**
 * Convenience hook for mobile detection (< 768px)
 * @returns {boolean} - True if viewport is mobile size
 */
export function useIsMobile() {
  return useMediaQuery('(max-width: 767px)');
}

/**
 * Convenience hook for tablet detection (768px - 1023px)
 * @returns {boolean} - True if viewport is tablet size
 */
export function useIsTablet() {
  const isMinTablet = useMediaQuery('(min-width: 768px)');
  const isMaxTablet = useMediaQuery('(max-width: 1023px)');
  return isMinTablet && isMaxTablet;
}

/**
 * Convenience hook for desktop detection (>= 1024px)
 * @returns {boolean} - True if viewport is desktop size
 */
export function useIsDesktop() {
  return useMediaQuery('(min-width: 1024px)');
}

/**
 * Returns current device type as a string
 * @returns {'mobile' | 'tablet' | 'desktop'} - Current device type
 */
export function useDeviceType() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  if (isMobile) return 'mobile';
  if (isTablet) return 'tablet';
  return 'desktop';
}
